import {
  ChangeDetectorRef,
  Component,
  forwardRef,
  inject,
} from '@angular/core';
import { RoundAndFormatPipe } from 'src/app/shared/pipes/round-and-format.pipe';
import { AnalyticsColumnData } from '../analytics-table-columns.const';
import {
  ANALYTICS_BASE_TOKEN,
  AnalyticsTablesBaseComponent,
} from '../analytics-tables-base/analytics-tables-base.component';
import {
  AnalyticsReportsColumns,
  AnalyticsReportsTypes,
} from '../analytics.conts';
import {
  ANALYTICS_SOURCE,
  ANALYTICS_TYPE,
  AnalyticsData,
  BackTestingTableData,
  BacktestedProbabilityOfLossTableData,
  ForwardLookingProbabilityOfLossTableData,
  ForwardTestingTableData,
  ProbabilityOfLossTableData,
  ProbabilityOfReturnsTableData,
  RiskRewardTableData,
  TableData,
  ValueForMoneyCloudRiskTableData,
  ValueForMoneyFVCTableData,
} from '../analytics.interface';

@Component({
  selector: 'app-stress-testing-tables',
  templateUrl: './stress-testing-tables.component.html',
  styleUrls: [
    './stress-testing-tables.component.scss',
    '../analytics-tables-base/analytics-tables-base.component.scss',
  ],
  providers: [
    {
      provide: ANALYTICS_BASE_TOKEN,
      useExisting: forwardRef(() => StressTestingTablesComponent),
    },
  ],
})
export class StressTestingTablesComponent extends AnalyticsTablesBaseComponent {
  // Columns:
  columns = AnalyticsReportsColumns.STRESS_TESTING;

  backTestingData: BackTestingTableData[];
  forwardTestingData: ForwardTestingTableData[];
  probabilityOfReturnsData: ProbabilityOfReturnsTableData[];
  backtestedProbabilityOfLossData: BacktestedProbabilityOfLossTableData[];
  forwardLookingProbabilityOfLossData: ForwardLookingProbabilityOfLossTableData[];
  riskRewardData: RiskRewardTableData[];
  valueForMoneyFVCData: ValueForMoneyFVCTableData[];
  probabilityOfLossData: ProbabilityOfLossTableData[];
  valueForMoneyCloudRiskData: ValueForMoneyCloudRiskTableData[];

  backFvcAsOfDate: string;
  forwardFvcAsOfDate: string;
  performanceFvcAsOfDate: string;
  riskRewardFvcAsOfDate: string;
  valueForMoneyFVCAsOfDate: string;
  forwardCloudRiskAsOfDate: string;
  valueForMoneyCloudRiskAsOfDate: string;

  valueForMoneyHeader: string = 'Value for Money';
  valueForMoneySubHeader: string;

  roundAndFormatPipe = new RoundAndFormatPipe(inject(ChangeDetectorRef));

  prepareData(): void {
    this.backTestingData = this.analyticsService.getBackTestingTableData(
      this.data
    );
    this.forwardTestingData = this.analyticsService.getForwardTestingTableData(
      this.data
    );
    this.probabilityOfReturnsData =
      this.analyticsService.getProbabilityOfReturnsTableData(this.data);
    this.backtestedProbabilityOfLossData =
      this.analyticsService.getBacktestedProbabilityOfLossTableData(this.data);
    this.forwardLookingProbabilityOfLossData =
      this.analyticsService.getForwardLookingProbabilityOfLossTableData(
        this.data
      );
    this.riskRewardData = this.analyticsService.getRiskRewardTableData(
      this.data
    );
    this.probabilityOfLossData =
      this.analyticsService.getProbabilityOfLossTableData(this.data);
    this.valueForMoneyFVCData =
      this.analyticsService.getValueForMoneyFVCTableData(this.data);
    this.valueForMoneyCloudRiskData =
      this.analyticsService.getValueForMoneyCloudRiskTableData(this.data);
    this.setValueForMoneyHeaderAndSubHeader(
      this.valueForMoneyFVCData ?? this.valueForMoneyCloudRiskData
    );

    this.setTableAsOfDates(this.data);

    this.amendTableColumns();
  }

  amendTableColumns(): void {
    // NB we are dynamically altering the table columns for the two tables below,
    // which have been identified as having different variants based on the return
    // data. We can implement the same logic to filter out columns with no data on
    // any table using this same logic, if required.

    this.columns.riskReward = this.getFilteredTableColumns(
      this.riskRewardData,
      this.columns.riskReward
    );

    this.columns.valueForMoneyFVC = this.getFilteredTableColumns(
      this.valueForMoneyFVCData,
      this.columns.valueForMoneyFVC
    );

    this.columns.forwardLookingProbabilityOfLoss = this.getFilteredTableColumns(
      this.forwardLookingProbabilityOfLossData,
      this.columns.forwardLookingProbabilityOfLoss
    );

    this.columns.probabilityOfReturns = this.getFilteredTableColumns(
      this.probabilityOfReturnsData,
      this.columns.probabilityOfReturns
    );
  }

  getFilteredTableColumns(
    tableData: TableData[],
    allPossibleTableColumns: AnalyticsColumnData[]
  ): AnalyticsColumnData[] {
    if (!tableData?.length) {
      return allPossibleTableColumns;
    }
    const columnNames =
      this.analyticsService.getFilteredColumnNamesBasedOnTableData(tableData);
    return allPossibleTableColumns.filter((obj) =>
      columnNames.find((columnKey) => columnKey === obj.prop)
    );
  }

  setTableAsOfDates(data: AnalyticsData[]): void {
    this.backFvcAsOfDate = this.analyticsService.getCreationDate(
      data,
      ANALYTICS_SOURCE.FVC,
      ANALYTICS_TYPE.BACK_TYPE
    );
    this.forwardFvcAsOfDate = this.analyticsService.getCreationDate(
      data,
      ANALYTICS_SOURCE.FVC,
      ANALYTICS_TYPE.FORWARD_TEST
    );
    this.performanceFvcAsOfDate = this.analyticsService.getCreationDate(
      data,
      ANALYTICS_SOURCE.FVC,
      ANALYTICS_TYPE.PERFORMANCE_TEST
    );
    this.riskRewardFvcAsOfDate = this.analyticsService.getCreationDate(
      data,
      ANALYTICS_SOURCE.FVC,
      ANALYTICS_TYPE.RISK_REWARD
    );
    this.valueForMoneyFVCAsOfDate = this.analyticsService.getCreationDate(
      data,
      ANALYTICS_SOURCE.FVC,
      ANALYTICS_TYPE.VFM_TEST
    );
    this.forwardCloudRiskAsOfDate = this.analyticsService.getCreationDate(
      data,
      ANALYTICS_SOURCE.CLOUD_RISK,
      ANALYTICS_TYPE.FORWARD_TEST
    );
    this.valueForMoneyCloudRiskAsOfDate = this.analyticsService.getCreationDate(
      data,
      ANALYTICS_SOURCE.CLOUD_RISK,
      ANALYTICS_TYPE.VFM_TEST
    );
  }

  setValueForMoneyHeaderAndSubHeader(
    valueForMoneyData:
      | ValueForMoneyFVCTableData[]
      | ValueForMoneyCloudRiskTableData[]
  ): void {
    if (!valueForMoneyData?.length) {
      this.valueForMoneyHeader = 'Value for Money';
      return;
    }

    if (valueForMoneyData[0]?.hurdleRate) {
      this.valueForMoneyHeader = 'Value for Money';
      this.valueForMoneySubHeader = `(hurdle rate: ${this.roundAndFormatPipe.transform(
        valueForMoneyData[0].hurdleRate,
        2
      )})`;
    }
  }

  isDataAvailable(): boolean {
    return !!(
      this.backTestingData ||
      this.forwardTestingData ||
      this.probabilityOfReturnsData ||
      this.backtestedProbabilityOfLossData ||
      this.forwardLookingProbabilityOfLossData ||
      this.riskRewardData ||
      this.valueForMoneyFVCData ||
      this.probabilityOfLossData ||
      this.valueForMoneyCloudRiskData
    );
  }

  get reportType(): AnalyticsReportsTypes {
    return AnalyticsReportsTypes.STRESS;
  }
}
